import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import FaceNeutral from "assets/icons/FaceNeutral.svg";
import BodyHeader from "../BodyHeader";
import InputCell from "assets/Home/InputCell.svg";
import SearchIcon from "assets/icons/Search.svg";
import FileIcon from "assets/icons/FaceNeutral.svg";
import Send from "assets/icons/Send.svg";
import Attachment from "assets/icons/Attachment.svg";
import APPLICATION_SETTINGS from "../../../../settings";
import Cookies from "universal-cookie";
import { useUser } from "../../../context/UserContext";
import { useNotifications } from "../../../context/NotificationContext";
import { formatRelativeTime } from "../../../utils/FormatDate";
import { humanizeEnum, formatCurrency, formatScheduledTime } from "../../../utils/utils";
import ButtonComponent from "../../../Ui/Button";
import { useCurrentSelectedData } from "../../../context/CurrentSelectedDataContext";
import ProposalRequestAcceptDeny from "../ModalData/ProposalRequestAcceptDeny";
import RescheduleRequestAcceptDeny from "../ModalData/RescheduleRequestAcceptDeny";
import WarrantyClaimReviewModal from "../ModalData/WarrantyClaimReviewModal";
import WarrantyScheduleRepairModal from "../ModalData/WarrantyScheduleRepairModal";
import { JobsApi, WarrantyRequests } from "../../../../service.js";
const API_BASE = APPLICATION_SETTINGS.VITE_API_BASE || "http://127.0.0.1:8002";
const backendHost = API_BASE.replace(/^https?:\/\//, "")
  .replace(/\/api\/?$/, "")
  .replace(/\/$/, "");

/**
 * Helper to retroactively format any unformatted date strings in message bodies.
 * E.g. "2026-02-03|allDay" -> "2/3/2026 (All Day)"
 */
const formatMessageBody = (body) => {
  if (!body || typeof body !== "string") return body;
  return body.replace(/\d{4}-\d{2}-\d{2}\|[a-zA-Z]+/g, (match) => {
    return formatScheduledTime(match);
  });
};

// Invoice-opened messages currently include a raw URL (e.g. "View in Open Invoices: http://...").
// For aesthetics, hide the URL in the UI and render a button instead.
const extractInvoiceLink = (body) => {
  if (!body || typeof body !== "string") return null;
  const m = body.match(
    /(?:View in Open Invoices|View invoice):\s*(https?:\/\/[^\s]+|\/[^\s]+)/i
  );
  if (!m) return null;
  const url = m[1];
  const text = body
    .replace(m[0], "")
    .replace(/\s{2,}/g, " ")
    .trim();
  return { url, text: text || body };
};

// Proposal amounts can be either a single value ("250") or a range ("200 - 350").
// formatCurrency() only supports single numeric values; when a range is provided it will
// truncate to the first number. Preserve the original range string when detected.
const formatProposalAmount = (rawAmount) => {
  if (rawAmount === null || rawAmount === undefined) return "Not specified";
  const s = String(rawAmount).trim();
  if (!s) return "Not specified";

  const rangeMatch = s.match(
    /(-?\$?\s*\d[\d,]*(?:\.\d+)?)\s*[-–]\s*(-?\$?\s*\d[\d,]*(?:\.\d+)?)/
  );
  if (rangeMatch) return s;

  return formatCurrency(s);
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.detail) return data.detail;

  if (data && typeof data === "object") {
    const firstValue = Object.values(data)[0];
    if (Array.isArray(firstValue) && firstValue[0]) return firstValue[0];
    if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  }

  return fallbackMessage;
};

const Messages = ({
  header = true,
  title = "Messages",
  messages = [],
  searchQuery = "",
}) => {
  // UI state

  const { userData } = useUser();
  const {
    globalWsConnected,
    globalWsError,
    unreadCount,
    setUnreadCount,
    threadUnreadCounts,
    setThreadUnreadCounts,
    updateUnreadCount,
    clearThreadUnreadCount,
    realTimeMessages,
    setRealTimeMessages,
    messageListTyping,
    setMessageListTyping,
    newThreads,
    setNewThreads,
    refreshMessageList,
    refreshTrigger,
    setCurrentUser,
    setSelectedThread,
  } = useNotifications();

  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null); // left list selection
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [showChatList, setShowChatList] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Chat state
  const [isConnected, setIsConnected] = useState(false);
  const [wsError, setWsError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]); // normalized message array
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [userLastSeen, setUserLastSeen] = useState({});
  const [messageList, setMessageList] = useState(messages); // Local state for message list

  // Debug logging for threadUnreadCounts
  useEffect(() => {
    console.log("[Messages] threadUnreadCounts updated:", threadUnreadCounts);
  }, [threadUnreadCounts]);

  // Composing
  const [messageInput, setMessageInput] = useState("");
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Change Proposal Respond Modal state
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [respondProposal, setRespondProposal] = useState(null);
  const [respondSubmitting, setRespondSubmitting] = useState(false);
  // Local override for proposal status so we can hide the button instantly after action
  const [localProposalStatus, setLocalProposalStatus] = useState(null);
  // Local override for reschedule status to hide UI instantly after decision
  const [localRescheduleStatus, setLocalRescheduleStatus] = useState(null);
  // Reschedule Respond Modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleToRespond, setRescheduleToRespond] = useState(null);
  const [rescheduleUiAction, setRescheduleUiAction] = useState(null);
  // Warranty Claim Review Modal state
  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const [warrantyClaim, setWarrantyClaim] = useState(null);
  const [warrantyClaimLoading, setWarrantyClaimLoading] = useState(false);
  const [warrantyReviewNotes, setWarrantyReviewNotes] = useState("");
  const [warrantyDecisionSubmitting, setWarrantyDecisionSubmitting] = useState(false);
  const [showWarrantyScheduleModal, setShowWarrantyScheduleModal] = useState(false);
  const [warrantyScheduleRequestId, setWarrantyScheduleRequestId] = useState(null);
  const [warrantyScheduleSubmitting, setWarrantyScheduleSubmitting] = useState(false);

  // Currently selected job (used across job detail panels)
  const { currentSelectedData, setCurrentSelectedData } = useCurrentSelectedData();

  // Refs
  const wsRef = useRef(null);
  const messageEndRef = useRef(null);
  const messagesContainerRef = useRef(null); // Ref for the scrollable messages container
  const messageInputRef = useRef(null);
  const typingStopTimerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const cookies = new Cookies();
  const token = cookies.get("token");

  // Only owners (HOMEOWNER or LANDLORD) may respond to change proposals
  const isOwner = useMemo(() => {
    const role = (userData?.accountType || "").toUpperCase();

    console.log('user role', role, userData)

    return ["HOMEOWNER", "STR_HOST", "LANDLORD", "PROPERTY_MANAGER", "FAMILY_MEMBER"].includes(role);
  }, [userData?.accountType]);

  const canReviewWarrantyClaim = useMemo(() => {
    const role = String(userData?.accountType || "").toUpperCase();
    return role === "VENDOR" || Boolean(userData?.is_staff || userData?.isStaff);
  }, [userData?.accountType, userData?.isStaff, userData?.is_staff]);

  
  // Detect proposal notification messages so we can render them as a card
  // but not also as a regular chat bubble

  // Function to scroll messages container to bottom
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  // API function to load chat history
  const loadChatHistory = async (threadId) => {
    console.log("threadIdk kjk", threadId);
    if (!threadId || !token) {
      return [];
    }

    try {
      setIsLoadingHistory(true);

      // Ask backend to mark returned messages as read for this user
      const response = await fetch(
        `${API_BASE}/api/chat/threads/${threadId}/messages/?mark_read=true`,
        {
          method: "GET",
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log('messages', response)

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to load messages: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();

      const normalizedMessages = (data.messages || data || []).map(
        normalizeServerMessage
      );

      setChatMessages(normalizedMessages);
      setIsLoadingHistory(false);

      // Scroll to bottom after loading
      // setTimeout(() => {
      //   if (messageEndRef.current) {
      //     messageEndRef.current.scrollIntoView({
      //       behavior: "smooth",
      //       block: "end",
      //       inline: "nearest",
      //     });
      //   }
      // }, 100);

      return normalizedMessages;
    } catch (error) {
      console.error("Error loading chat history:", error);

      setIsLoadingHistory(false);
      setChatMessages([]);
      return [];
    }
  };

  // Handlers for Change Request modal
  const handleOpenRespondModal = (proposal, uiAction = null) => {
    let nextProposal = proposal ?? selectedMessageData?.change_proposal ?? null;
    if (!nextProposal) {
      const proposalId = uiAction?.change_proposal_id ?? uiAction?.changeProposalId ?? null;
      if (proposalId) nextProposal = { id: proposalId };
    }
    setRespondProposal(nextProposal);
    setShowRespondModal(true);
  };

  const handleCloseRespondModal = () => setShowRespondModal(false);

  // Handlers for Reschedule modal
  const handleOpenRescheduleModal = (reschedule, uiAction = null) => {
    // Prefer message-level reschedule data, fallback to thread-level
    const rs = reschedule ?? selectedMessageData?.reschedule_request ?? null;
    // Merge ui_action fields that the modal might need (e.g., availability, proposed date/time)
    const merged = {
      ...(rs || {}),
      ...(uiAction?.days_available ? { days_available: uiAction.days_available } : {}),
      ...(uiAction?.daysAvailable ? { daysAvailable: uiAction.daysAvailable } : {}),
      ...(uiAction?.proposed_date ? { proposed_date: uiAction.proposed_date } : {}),
      ...(uiAction?.proposed_time ? { proposed_time: uiAction.proposed_time } : {}),
      ...(uiAction?.reschedule_id ? { id: uiAction.reschedule_id } : {}),
    };
    setRescheduleToRespond(merged);
    setRescheduleUiAction(uiAction || null);
    setShowRescheduleModal(true);
  };
  const handleCloseRescheduleModal = () => setShowRescheduleModal(false);

  const handleOpenWarrantyModal = async (uiAction = null) => {
    const rawClaimId =
      uiAction?.warranty_request_id ?? uiAction?.warrantyRequestId ?? null;
    const claimId =
      typeof rawClaimId === "string" ? rawClaimId.trim() : rawClaimId;
    if (!claimId) {
      window.alert("Warranty claim details are not available for this message.");
      return;
    }

    setShowWarrantyModal(true);
    setWarrantyClaim(null);
    setWarrantyReviewNotes("");
    setWarrantyClaimLoading(true);

    try {
      const response = await WarrantyRequests.getById(claimId);
      const data = response?.data || response;
      setWarrantyClaim(data);
      setWarrantyReviewNotes(data?.reviewNotes || "");
    } catch (error) {
      console.error("Error loading warranty claim:", error);
      setShowWarrantyModal(false);
      window.alert(
        error?.response?.data?.detail ||
          "Could not load the warranty claim details."
      );
    } finally {
      setWarrantyClaimLoading(false);
    }
  };

  const handleCloseWarrantyModal = () => {
    setShowWarrantyModal(false);
  };

  const handleOpenWarrantyScheduleModal = (uiAction = null) => {
    const rawRequestId =
      uiAction?.request_id ??
      uiAction?.requestId ??
      warrantyClaim?.maintenanceRequestDetails?.id ??
      null;
    const requestId =
      typeof rawRequestId === "string" ? rawRequestId.trim() : rawRequestId;

    if (!requestId) {
      window.alert("Warranty scheduling is not available for this message.");
      return;
    }

    setWarrantyScheduleRequestId(requestId);
    setShowWarrantyScheduleModal(true);
  };

  const handleCloseWarrantyScheduleModal = () => {
    setShowWarrantyScheduleModal(false);
    setWarrantyScheduleRequestId(null);
    setWarrantyScheduleSubmitting(false);
  };

  const submitWarrantyDecision = async (action) => {
    if (!warrantyClaim?.id || warrantyDecisionSubmitting) return false;
    if (action === "deny" && !warrantyReviewNotes.trim()) {
      window.alert("Add a reason before denying this claim.");
      return false;
    }

    try {
      setWarrantyDecisionSubmitting(true);
      const response =
        action === "approve"
          ? await WarrantyRequests.approve(warrantyClaim.id, {
              review_notes: warrantyReviewNotes,
            })
          : await WarrantyRequests.deny(warrantyClaim.id, {
              review_notes: warrantyReviewNotes.trim(),
            });

      const data = response?.data || response;
      setWarrantyClaim(data);
      setWarrantyReviewNotes(data?.reviewNotes || warrantyReviewNotes);

      try {
        if (selectedThreadId) await loadChatHistory(selectedThreadId);
      } catch (e) {
        console.warn("Failed to refresh chat history after warranty decision", e);
      }
      try {
        if (typeof refreshMessageList === "function") refreshMessageList();
      } catch (e) {
        console.warn("Failed to refresh message list after warranty decision", e);
      }

      return true;
    } catch (error) {
      console.error("Error reviewing warranty claim:", error);
      window.alert(
        error?.response?.data?.detail ||
          "Something went wrong while reviewing the warranty claim."
      );
      return false;
    } finally {
      setWarrantyDecisionSubmitting(false);
    }
  };

  const handleApproveWarranty = async () => {
    await submitWarrantyDecision("approve");
  };

  const handleDenyWarranty = async () => {
    await submitWarrantyDecision("deny");
  };

  const handleSubmitWarrantySchedule = async (values) => {
    if (!warrantyScheduleRequestId) {
      window.alert("Warranty scheduling is not available for this request.");
      return;
    }

    const payload = {
      rescheduleReason: values.rescheduleReason,
      daysAvailable: values.days_available,
    };

    try {
      setWarrantyScheduleSubmitting(true);
      await JobsApi.proposeReschedule(warrantyScheduleRequestId, payload);

      handleCloseWarrantyScheduleModal();
      setShowWarrantyModal(false);

      try {
        if (selectedThreadId) await loadChatHistory(selectedThreadId);
      } catch (e) {
        console.warn("Failed to refresh chat history after warranty scheduling", e);
      }
      try {
        if (typeof refreshMessageList === "function") refreshMessageList();
      } catch (e) {
        console.warn("Failed to refresh message list after warranty scheduling", e);
      }

      window.alert("Warranty schedule request sent to the customer.");
    } catch (error) {
      console.error("Error scheduling warranty repair:", error);
      window.alert(
        getApiErrorMessage(
          error,
          "Could not submit the warranty schedule request."
        )
      );
    } finally {
      setWarrantyScheduleSubmitting(false);
    }
  };

  const respondToProposal = async (action) => {
    if (respondSubmitting) return;
    try {
      const requestId = selectedMessageData?.request || selectedItem?.request;
      const rawProposalId = respondProposal?.id ?? respondProposal?.proposal_id;
      const proposalId = typeof rawProposalId === "string" ? rawProposalId.trim() : rawProposalId;

      if (!token) {
        window.alert("You must be logged in to perform this action.");
        return false;
      }
      if (!requestId || !proposalId) {
        console.warn("Missing requestId or proposalId", { requestId, proposalId });
        window.alert("Unable to process request: missing proposal or request identifier.");
        return false;
      }

      setRespondSubmitting(true);
      const url = `${API_BASE}/api/maintenance-requests/${requestId}/proposals/${proposalId}/${action}/`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      let payload = null;
      try {
        payload = await res.json();
      } catch (_) {}

      if (!res.ok) {
        const detail = payload?.detail || `Request failed (${res.status})`;
        window.alert(detail);
        return false;
      }

      // Success
      if (payload?.detail) {
        // Basic feedback
        console.log(payload.detail);
      }

      // Refresh current thread messages and left list if possible
      try {
        if (selectedThreadId) await loadChatHistory(selectedThreadId);
      } catch (e) {
        console.warn("Failed to refresh chat history after proposal response", e);
      }
      try {
        if (typeof refreshMessageList === "function") refreshMessageList();
      } catch (e) {
        console.warn("Failed to refresh message list after proposal response", e);
      }

      return true;
    } catch (e) {
      console.error("Error responding to proposal:", e);
      window.alert("Something went wrong while processing your request.");
      return false;
    } finally {
      setRespondSubmitting(false);
    }
  };

  const handleAcceptChange = async () => {
    const ok = await respondToProposal("approve");
    if (ok) {
      // Immediately hide the button by marking locally as approved
      setLocalProposalStatus("APPROVED");
      setShowRespondModal(false);
    }
  };

  const handleDenyChange = async () => {
    const ok = await respondToProposal("deny");
    if (ok) {
      // Immediately hide the button by marking locally as denied
      setLocalProposalStatus("DENIED");
      setShowRespondModal(false);
    }
  };

  // Accept/Deny for reschedule via backend endpoints
  const handleAcceptReschedule = async (chosenOption = null) => {
    // Build request body per backend spec
    let body = null;
    if (chosenOption) {
      body = { chosen_option: chosenOption };
    } else {
      // Try fallback explicit form if provided in payload
      const rs = rescheduleToRespond ?? selectedMessageData?.reschedule_request ?? {};
      const chosen_date = rs?.newDate || rs?.new_date || rs?.proposed_date || rs?.date || rs?.requested_date || null;
      const chosen_time = rs?.newTime || rs?.new_time || rs?.proposed_time || rs?.time || rs?.requested_time || null;

      // If explicit legacy fields present, send them; otherwise, empty body is allowed for single, unambiguous choice
      if (chosen_date || chosen_time) {
        body = { ...(chosen_date ? { chosen_date } : {}), ...(chosen_time ? { chosen_time } : {}) };
      } else {
        body = null; // let backend infer when unambiguous
      }
    }

    // Keep a copy of the chosen option (YYYY-MM-DD|slot) for optimistic UI update
    let effectiveOption = chosenOption;
    try {
      if (!effectiveOption) {
        const rs = rescheduleToRespond ?? selectedMessageData?.reschedule_request ?? {};
        let raw = rs?.days_available || rs?.daysAvailable || [];
        if (typeof raw === "string") {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) raw = parsed;
          } catch (_) {
            raw = [];
          }
        }
        if (Array.isArray(raw) && raw.length === 1 && typeof raw[0] === "string") {
          effectiveOption = raw[0];
        }
      }
    } catch (_) {}

    const ok = await respondToReschedule("approve", rescheduleUiAction, body);
    if (ok) {
      setLocalRescheduleStatus("APPROVED");
      setShowRescheduleModal(false);

      // Optimistically update the currently selected job's scheduled time
      try {
        const requestId = selectedMessageData?.request || selectedItem?.request;
        if (
          effectiveOption &&
          requestId &&
          typeof setCurrentSelectedData === "function" &&
          currentSelectedData &&
          String(currentSelectedData?.id) === String(requestId)
        ) {
          // Parse option into date + time
          let scheduledDate = null;
          let scheduledTime = null;
          try {
            const [d, tRaw] = String(effectiveOption).split("|");
            scheduledDate = d || null;
            const lower = String(tRaw || "").trim().toLowerCase();
            scheduledTime = lower === "allday" ? "allDay" : lower || null;
          } catch (_) {}

          setCurrentSelectedData({
            ...currentSelectedData,
            daysAvailable: [effectiveOption],
            days_available: [effectiveOption],
            ...(scheduledDate ? { scheduledDate } : {}),
            ...(scheduledDate ? { scheduled_date: scheduledDate } : {}),
            ...(scheduledTime ? { scheduledTime } : {}),
            ...(scheduledTime ? { scheduled_time: scheduledTime } : {}),
          });
        }
      } catch (e) {
        console.warn("Failed to update local selected job after reschedule accept", e);
      }
    }
  };

  const handleDenyReschedule = async (reason = "") => {
    const body = reason ? { reason } : {};
    const ok = await respondToReschedule("deny", rescheduleUiAction, body);
    if (ok) {
      setLocalRescheduleStatus("DENIED");
      setShowRescheduleModal(false);
    }
  };

  const normalizeActionUrl = (url) => {
    if (!url) return null;

    try {
      const apiBaseUrl = new URL(API_BASE);
      const resolvedUrl = new URL(url, apiBaseUrl);
      const normalizedPath = `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
      return new URL(
        normalizedPath,
        `${apiBaseUrl.protocol}//${apiBaseUrl.host}`
      ).toString();
    } catch (error) {
      console.warn("Failed to normalize action URL", url, error);
      return url;
    }
  };

  // Helper to POST to provided approve/deny URLs (reschedule flow prefers direct URLs)
  const postToActionUrl = async (url, body = null) => {
    if (!url) return { ok: false, payload: null };
    if (!token) {
      window.alert("You must be logged in to perform this action.");
      return { ok: false, payload: null };
    }
    try {
      const headers = {
        Authorization: `Token ${token}`,
      };
      const opts = {
        method: "POST",
        headers,
      };
      if (body !== null && body !== undefined) {
        opts.headers = { ...headers, "Content-Type": "application/json" };
        opts.body = JSON.stringify(body);
      }
      const res = await fetch(normalizeActionUrl(url), opts);
      let payload = null;
      try {
        payload = await res.json();
      } catch (_) {}
      if (!res.ok) {
        const detail = payload?.detail || `Request failed (${res.status})`;
        window.alert(detail);
        return { ok: false, payload };
      }
      return { ok: true, payload };
    } catch (e) {
      console.error("Error calling action URL:", e);
      window.alert("Something went wrong while processing your request.");
      return { ok: false, payload: null };
    }
  };

  // Reschedule approve/deny using ui_action URLs if present; fallback constructs URL
  const respondToReschedule = async (action, uiAction, body = null) => {
    const reschedule =
      rescheduleToRespond ?? selectedMessageData?.reschedule_request ?? null;
    const requestId =
      uiAction?.request_id ??
      uiAction?.requestId ??
      selectedMessageData?.request ??
      selectedItem?.request;
    const rescheduleId =
      uiAction?.reschedule_id ??
      uiAction?.rescheduleId ??
      reschedule?.id ??
      null;
    let url = null;
    if (requestId && rescheduleId) {
      url = `${API_BASE}/api/maintenance-requests/${requestId}/reschedules/${rescheduleId}/${action}/`;
    }
    if (!url && uiAction && (action === "approve" || action === "deny")) {
      url = action === "approve" ? uiAction.approve_url : uiAction.deny_url;
    }

    const { ok } = await postToActionUrl(url, body);
    if (ok) {
      try {
        if (selectedThreadId) await loadChatHistory(selectedThreadId);
      } catch {}
      try {
        if (typeof refreshMessageList === "function") refreshMessageList();
      } catch {}
    }
    return ok;
  };

  // Build WS URL helper (must use browser protocol/host for proxies to upgrade)
  const wsUrl = useMemo(() => {
    if (!selectedThreadId) return null;

    // 1. Determine protocol: if API is https, WS must be wss
    const protocol = API_BASE.startsWith("https") ? "wss:" : "ws:";

    // 2. Always use the backendHost derived from API_BASE
    // (backendHost is already defined at the top of your file)
    const host = backendHost;

    const tokenQS = token ? `?token=${encodeURIComponent(token)}` : "";
    return `${protocol}//${host}/ws/chat/thread/${selectedThreadId}/${tokenQS}`;
  }, [selectedThreadId, token]);

  // Filtered left list
  const isUser = (msg) => {
    if (!userData?.id) return false;
    // Compare both sender_id (from normalized message) and sender (from API response)
    return (
      String(msg?.sender_id) === String(userData.id) ||
      String(msg?.sender) === String(userData.id)
    );
  };

  // Helper to de‑duplicate threads by id/threadId while preserving order
  const dedupeThreads = useCallback((arr) => {
    const map = new Map();
    for (const m of arr) {
      const key = m?.threadId ?? m?.id;
      if (key == null) continue;
      if (!map.has(key)) map.set(key, m);
    }
    return Array.from(map.values());
  }, []);

  const filteredMessages = useMemo(() => {
    // Combine local message list with new threads, but avoid duplicates
    const allMessages = dedupeThreads([...newThreads, ...messageList]);

    // If title is "New Messages", filter to show only unread messages
    // if (title === "New Messages") {
    //   return allMessages.filter((message) => {
    //     const threadId = message.threadId || message.id;
    //     // Use unread count from global notification context
    //     const unreadCount = threadUnreadCounts[threadId] || 0;

    //     // Show messages that have unread count > 0
    //     return unreadCount > 0;
    //   });
    // }

    // For regular "Messages" title, show all messages with search filter
    const q = (localSearchQuery || "").toLowerCase();
    if (!q) return allMessages;
    return allMessages.filter((m) => {
      const name = m.last_message?.sender_name || m.name || "";
      return name.toLowerCase().includes(q);
    });
  }, [localSearchQuery, messageList, newThreads, title, dedupeThreads]);

  // Whether there is a specific message in the thread that carries a RESCHEDULE_REQUEST ui_action
  const hasRescheduleActionMessage = useMemo(() => {
    try {
      return chatMessages.some((m) => {
        const t = m?.ui_action?.type;
        return t && String(t).toUpperCase() === "RESCHEDULE_REQUEST";
      });
    } catch (_) {
      return false;
    }
  }, [chatMessages]);


  // The selected item (left list) for header/proposal block
  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null;
    // Check both local message list and new threads (de‑duplicated)
    const allMessages = dedupeThreads([...newThreads, ...messageList]);
    const item = allMessages.find((m) => m.id === selectedItemId) || null;
    if (!item) return null;

    // Parse proposal information from last_message body if it contains proposal data
    const hasProposal =
      item.last_message?.body?.includes("📩 New proposal submitted") ||
      item.last_message?.body?.includes("Amount:") ||
      item.last_message?.body?.includes("Proposal ID:") ||
      item.last_message?.body?.includes("proposed a change");

    let proposal = null;
    if (hasProposal && item.last_message?.body) {
      const body = item.last_message.body;
      const amountMatch = body.match(/Amount:\s*([^\n]+)/) || body.match(/New estimate:\s*([^\n]+)/);
      const timeframeMatch = body.match(/Timeframe:\s*([^\n]+)/);
      const messageMatch = body.match(/Message:\s*([^\n]+)/) || body.match(/Additional work:\s*([^\n]+)/);
      const proposalIdMatch = body.match(/Proposal ID:\s*([^\n]+)/);
      // Prefer issuetype from the thread item; fall back to any structured body field if present
      const requestIssueTypeRaw = item?.issuetype || item?.issueType || body?.issuetype || null;

      console.log('proposal body', body)

      proposal = {
        title: `Job: ${humanizeEnum(requestIssueTypeRaw) || "Unknown"}`,
        date: new Date(item.last_message_at).toLocaleDateString(),
        estimatedCost: amountMatch?.[1] ? formatProposalAmount(amountMatch[1]) : "Not specified",
        timeframe: timeframeMatch?.[1] ? formatScheduledTime(timeframeMatch[1].trim()) : "Not specified",
        message: messageMatch?.[1] || "No message provided",
        // issueType: requestIssueTypeMatch?.[1] || "Not specified",
        // subIssueType: requestSubIssueTypeMatch?.[1] || "Not specified",
        id: proposalIdMatch?.[1] || null,
      };
    }

    // Get the other participant's name (not the current user)
    const getOtherParticipantName = () => {
      if (!item.participants || !Array.isArray(item.participants)) {
        return item.last_message?.sender_name || "Unknown User";
      }

      // Find the participant who is not the current user
      const otherParticipant = item.participants.find(
        (participant) => participant.id !== userData?.id
      );

      return (
        otherParticipant?.name ||
        otherParticipant?.email ||
        item.last_message?.sender_name ||
        "Unknown User"
      );
    };

    return {
      ...item,
      name: getOtherParticipantName(),
      profileImage: "/default-avatar.png", // You can add profile image logic here
      isOnline: false, // Online status is handled directly in UI
      service: item.issuetype || "Property Service", // Use issuetype from API
      hasOffer: hasProposal,
      hasJob: false, // Set based on your business logic
      proposal: proposal,
      timestamp: new Date(item.last_message_at).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      lastMessage: item.last_message?.body || "No messages yet",
      chatMessages: chatMessages.map((msg) => ({
        sender: isUser(msg) ? "you" : "other",
        message: msg.body,
        time: new Date(msg.created_at).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      })),
    };
  }, [selectedItemId, chatMessages, newThreads, messages]);

  // Alias for selectedItem to match the UI expectations
  const selectedMessageData = selectedItem;

  // Determine if the current user can respond to a pending change proposal in the selected thread
  const canRespondToChange = useMemo(() => {
    if (!isOwner) return false;
    const topic = String((selectedMessageData?.topic || "").toString()).toUpperCase();
    // Prefer explicit presence of change_proposal with undecided status; fall back to topic check
    const proposal = selectedMessageData?.change_proposal || null;
    const rawStatus = localProposalStatus ?? proposal?.status;
    const statusNorm =
      rawStatus === null || rawStatus === undefined
        ? null
        : String(rawStatus).toUpperCase();

    // If we know it's decided, never show the button
    const isDecided = [
      "ACCEPTED",
      "APPROVED",
      "DENIED",
      "REJECTED",
      "DECLINED",
    ].includes(statusNorm || "");

    const isPending =
      statusNorm === null || statusNorm === undefined || statusNorm === "PENDING";

    // Show only if pending. If there is no proposal object (legacy), use topic fallback
    if (!selectedMessageData || isDecided) return false;
    return Boolean(proposal) ? isPending : topic === "CHANGE_PROPOSAL";
  }, [isOwner, selectedMessageData, localProposalStatus]);

  // Owner gate for showing reschedule actions; actual button display is determined per-message by ui_action
  const canRespondToReschedule = useMemo(() => {
    return !!isOwner;
  }, [isOwner]);

  // Reset local override when switching threads/items so we reflect server state for new selection
  useEffect(() => {
    setLocalProposalStatus(null);
    setLocalRescheduleStatus(null);
  }, [selectedItemId, selectedThreadId]);

  // Connect WS on selectedThreadId
  useEffect(() => {
    if (!selectedThreadId || !wsUrl) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setIsConnected(false);
      setWsError(null);
      return;
    }

    setIsConnected(false);
    setWsError(null);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    // ⏳ fail fast after 8s if onopen never fires
    const connectTimer = setTimeout(() => {
      if (wsRef.current === ws && ws.readyState !== WebSocket.OPEN) {
        console.warn("[WS] connection timeout");
        setWsError("Connection timeout");
        try {
          ws.close();
        } catch {}
      }
    }, 8000);

    ws.onopen = () => {
      clearTimeout(connectTimer);
      setIsConnected(true);

      // Add current user to online users when connected
      if (userData?.id) {
        setOnlineUsers((prev) => new Set([...prev, userData.id]));
        setUserLastSeen((prev) => ({
          ...prev,
          [userData.id]: new Date().toISOString(),
        }));
      }

      // Request online users list
      ws.send(JSON.stringify({ action: "get_online_users" }));
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);

        switch (payload.type) {
          case "history": {
            // Skip history processing since we load via API
            break;
          }
          case "message": {
            const msg = normalizeServerMessage(payload.data);

            setChatMessages((prev) =>
              prev.some((p) => p.id === msg.id) ? prev : [...prev, msg]
            );

            // Update real-time message for the message list
            setRealTimeMessages((prev) => ({
              ...prev,
              [msg.thread_id || selectedThreadId]: {
                body: msg.body,
                sender_name: msg.sender_name || "Unknown",
                timestamp: msg.created_at,
                sender_id: msg.sender_id,
              },
            }));

            // setTimeout(() => {
            //   if (messageEndRef.current) {
            //     messageEndRef.current.scrollIntoView({
            //       behavior: "smooth",
            //       block: "end",
            //       inline: "nearest",
            //     });
            //   }
            // }, 30);
            break;
          }
          case "typing_start":
            // Only show typing indicator for other users, not current user
            if (payload.user_id !== userData?.id) {
              setTypingUsers((prev) => ({
                ...prev,
                [payload.user_id]: payload.user_name || "Someone",
              }));

              // Track typing in message list
              setMessageListTyping((prev) => ({
                ...prev,
                [payload.thread_id || selectedThreadId]: {
                  user_id: payload.user_id,
                  user_name: payload.user_name || "Someone",
                  isTyping: true,
                },
              }));
            }
            break;
          case "typing_stop":
            setTypingUsers((prev) => {
              const c = { ...prev };
              delete c[payload.user_id];
              return c;
            });

            // Remove typing from message list
            setMessageListTyping((prev) => {
              const c = { ...prev };
              delete c[payload.thread_id || selectedThreadId];
              return c;
            });
            break;
          case "unread_count":
            if (payload.thread === selectedThreadId)
              setUnreadCount(payload.count || 0);
            break;
          case "user_online":
            setOnlineUsers((prev) => new Set([...prev, payload.user_id]));
            setUserLastSeen((prev) => ({
              ...prev,
              [payload.user_id]: new Date().toISOString(),
            }));
            break;
          case "user_offline":
            setOnlineUsers((prev) => {
              const newSet = new Set(prev);
              newSet.delete(payload.user_id);
              return newSet;
            });
            break;
          case "online_users_list":
            setOnlineUsers(new Set(payload.users || []));
            break;
          case "user_activity":
            // Track when user was last active (typing, viewing, etc.)
            setUserLastSeen((prev) => ({
              ...prev,
              [payload.user_id]: new Date().toISOString(),
            }));
            break;
          case "new_thread":
            // Add new thread to the messages list in real-time
            setNewThreads((prev) => {
              const newThread = {
                id: payload.thread_id,
                threadId: payload.thread_id,
                name: payload.other_user_name || "New User",
                last_message: {
                  body: payload.first_message || "New conversation started",
                  sender_name: payload.other_user_name || "New User",
                  sender_id: payload.other_user_id,
                  created_at: new Date().toISOString(),
                },
                last_message_at: new Date().toISOString(),
                timestamp: new Date().toISOString(),
                issuetype: payload.issue_type || "General",
                hasOffer: false,
                hasJob: false,
                isOnline: true, // New thread participants are considered online
              };
              return [newThread, ...prev];
            });
            break;
          case "error":
            setWsError(payload.error || "Server error");
            break;
          default:
            break;
        }
      } catch {
        // non-JSON fallback
        setChatMessages((prev) => [
          ...prev,
          {
            id: `tmp_${Date.now()}`,
            body: String(event.data || ""),
            sender_id: null,
            created_at: new Date().toISOString(),
          },
        ]);
      }
    };

    ws.onerror = (e) => {
      console.error("[WS] error", e);
      clearTimeout(connectTimer);
      setIsConnected(false);
      setWsError("WebSocket error");
    };

    ws.onclose = (ev) => {
      clearTimeout(connectTimer);
      setIsConnected(false);
      if (ev.code === 4401) setWsError("Unauthorized (token/session invalid)");
      else if (ev.code === 4403) setWsError("Forbidden (not a participant)");
      else if (ev.code === 4400) setWsError("Bad request");
      else if (ev.code === 1006) setWsError("Connection failed");
    };

    return () => {
      clearTimeout(connectTimer);
      if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
      if (wsRef.current === ws) {
        try {
          ws.close();
        } catch {}
        wsRef.current = null;
      }
    };
  }, [selectedThreadId, wsUrl]);

  // Set current user in notification context
  useEffect(() => {
    if (userData?.id) {
      setCurrentUser(userData.id);
    }
  }, [userData?.id, setCurrentUser]);

  // Set selected thread in notification context
  useEffect(() => {
    setSelectedThread(selectedThreadId);
  }, [selectedThreadId, setSelectedThread]);

  // Store the latest refreshMessageList function in a ref to avoid dependency issues
  const refreshMessageListRef = useRef(refreshMessageList);
  refreshMessageListRef.current = refreshMessageList;

  // Debounced refresh function to prevent rapid re-renders
  const debouncedRefreshMessageList = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      console.log(
        "[Messages] Calling refreshMessageList from debounced function"
      );
      refreshMessageListRef
        .current()
        .then((refreshedMessages) => {
          console.log(
            "[Messages] Received refreshed messages:",
            refreshedMessages
          );
          if (refreshedMessages && refreshedMessages.length > 0) {
            console.log(
              "[Messages] Updating messageList with",
              refreshedMessages.length,
              "messages"
            );
            setMessageList(refreshedMessages);
          } else {
            console.log("[Messages] No refreshed messages received");
          }
        })
        .catch((error) => {
          console.error("[Messages] Error refreshing message list:", error);
        });
    }, 300); // 300ms debounce
  }, []); // Empty dependency array to prevent infinite loop

  // Initialize message list from props and refresh from context
  useEffect(() => {
    console.log(
      "[Messages] useEffect triggered - messages prop:",
      messages.length,
      "messageList state:",
      messageList.length
    );
    setMessageList(messages);
    // Only refresh from context if messages prop is empty or different
    if (
      messages.length === 0 ||
      JSON.stringify(messages) !== JSON.stringify(messageList)
    ) {
      console.log(
        "[Messages] Calling debouncedRefreshMessageList due to prop change"
      );
      debouncedRefreshMessageList();
    }
  }, [messages]); // Removed debouncedRefreshMessageList from dependencies to prevent infinite loop

  // Listen for refresh trigger from context and refresh message list
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log(
        "[Messages] Refresh trigger received, calling debouncedRefreshMessageList"
      );
      debouncedRefreshMessageList();
    }
  }, [refreshTrigger, debouncedRefreshMessageList]);

  // Clear selection if selected item is not in filtered messages
  useEffect(() => {
    if (selectedItemId) {
      const isSelectedItemInFilteredResults = filteredMessages.some(
        (m) => m.id === selectedItemId
      );

      if (!isSelectedItemInFilteredResults) {
        setSelectedItemId(null);
        setSelectedMessage(null);
        setSelectedThreadId(null);
        setChatMessages([]);
        setShowChatList(true);
      }
    }
  }, [selectedItemId, filteredMessages]); // Use filteredMessages directly instead of its dependencies

  useEffect(() => {
    // Only auto-select if no item is selected, we have messages, and no search is active
    if (!selectedItemId && messageList.length > 0 && !localSearchQuery) {
      const first = messageList[0];
      const threadId = first.threadId || first.id;

      setSelectedItemId(first.id);
      setSelectedMessage(first.id);
      setShowChatList(false);

      // Load chat history for the first message
      if (threadId) {
        loadChatHistory(threadId)
          .then(() => {
            setSelectedThreadId(threadId);
          })
          .catch((error) => {
            setIsLoadingHistory(false);
          });
      } else {
        setSelectedThreadId(null);
        setChatMessages([]);
      }
    }
  }, [selectedItemId, messageList, localSearchQuery]);

  // Removed unused useEffect that was checking filtered results without performing any action

  // Mark all read when connected & thread changes
  useEffect(() => {
    if (
      isConnected &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN
    ) {
      wsRef.current.send(JSON.stringify({ action: "mark_all_read" }));
      // Only clear unread for the currently selected thread locally
      if (selectedThreadId) {
        clearThreadUnreadCount(selectedThreadId);
      }
    }
  }, [isConnected, selectedThreadId, clearThreadUnreadCount]);

  // Clear unread counts when chat is actively viewed (not just selected)
  useEffect(() => {
    if (
      selectedThreadId &&
      !showChatList &&
      threadUnreadCounts[selectedThreadId] > 0
    ) {
      clearThreadUnreadCount(selectedThreadId);
    }
  }, [
    selectedThreadId,
    showChatList,
    threadUnreadCounts,
    clearThreadUnreadCount,
  ]);

  // Clear unread counts when a chat is selected and viewed
  useEffect(() => {
    if (selectedItemId && !showChatList) {
      const currentUnreadCount = threadUnreadCounts[selectedItemId] || 0;
      if (currentUnreadCount > 0) {
        clearThreadUnreadCount(selectedItemId);
      }
    }
  }, [
    selectedItemId,
    showChatList,
    threadUnreadCounts,
    clearThreadUnreadCount,
  ]);

  // Focus input when chat opens
  useEffect(() => {
    if (!showChatList && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [showChatList]);

  // Auto-scroll to bottom when messages change - memoized to prevent unnecessary re-renders
  const memoizedChatMessages = useMemo(
    () => chatMessages,
    [chatMessages.length, chatMessages[chatMessages.length - 1]?.id]
  );

  useEffect(() => {
    if (memoizedChatMessages.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [memoizedChatMessages]);

  // Cleanup WebSocket connections on component unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Normalize server message shape
  function normalizeServerMessage(m) {
    if (!m) return m;
    console.log(m, "mfefcewdewdew");
    return {
      id: m.id ?? `tmp_${Math.random()}`,
      body: m.body ?? "",
      sender_id: m.sender_id ?? m.sender ?? null,
      sender: m.sender ?? m.sender_id ?? null, // Keep original sender field for comparison
      sender_name: m.sender_name ?? null,
      created_at: m.created_at ?? m.timestamp ?? new Date().toISOString(),
      issuetype: m.issuetype ?? "",
      ui_action: m.ui_action ?? null,
      topic: m.topic ?? null,
      reschedule_request: m.reschedule_request ?? null,
      // you can add attachments/type if backend sends them
    };
  }

  // UI handlers
  const handleMessageClick = async (itemId) => {
    setSelectedItemId(itemId);
    setSelectedMessage(itemId);
    setShowChatList(false);
    const allMessages = dedupeThreads([...newThreads, ...messageList]);
    const item = allMessages.find((m) => m.id === itemId);
    const threadId = item?.threadId || item?.id;

    // Clear unread count for this thread
    clearThreadUnreadCount(threadId);

    if (threadId) {
      // Load chat history first
      await loadChatHistory(threadId);
      // Then set the thread ID to establish WebSocket connection
      setSelectedThreadId(threadId);
    } else {
      setSelectedThreadId(null);
      setChatMessages([]);
    }

    // Prevent page scroll when switching chats
    // setTimeout(() => {
    //   window.scrollTo(0, 0);
    // }, 50);
  };

  const handleBackClick = () => setShowChatList(true);

  const handleSendMessage = () => {
    if (
      !messageInput.trim() ||
      !isConnected ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN
    )
      return;

    // Send with correct action name
    wsRef.current.send(
      JSON.stringify({
        action: "send_message",
        body: messageInput.trim(),
        thread_id: selectedThreadId,
        type: "text",
      })
    );

    setMessageInput("");
    // Stop typing indicator
    sendTyping(false);
  };

  const onKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const sendTyping = (isTyping) => {
    if (
      !isConnected ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN
    )
      return;
    wsRef.current.send(
      JSON.stringify({
        action: isTyping ? "typing_start" : "typing_stop",
        thread_id: selectedThreadId,
      })
    );
  };

  const onInputChange = (e) => {
    const val = e.target.value;
    setMessageInput(val);
    if (val.trim()) {
      sendTyping(true);
    } else {
      sendTyping(false);
    }
  };

  const handleReconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    setWsError(null);
    // The useEffect will automatically reconnect
  };

  // Render helpers

  return (
    <div className="flex flex-col w-full  tracking-[0.03em]  ">
      {header && (
        <div className="py-8">
          <BodyHeader title="Messages" />
        </div>
      )}

      <div
        className={`flex ${
          title == "New Messages" ? "md:h-[768px]" : "md:h-[938px]"
        } h-[700px] bg-white border border-[#DADADA] rounded-[12px]`}
      >
        {/* Chat List - Show on desktop always, on mobile only when showChatList is true */}
        <div
          className={`flex-1 border-r border-[#DADADA] flex max-w-[400px] flex-col ${
            !showChatList ? "hidden md:flex" : "flex"
          }`}
        >
          {/* Header */}
          <div className="py-4 tracking-[0.03em]  border-b border-[#DADADA] px-4">
            <h2 className="text-[32px] font-medium text-[#242424] mb-4">
              {title}
            </h2>

            <div className="relative flex gap-2">
              <input
                type="text"
                placeholder="Search by name"
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                className="max-w-[308px] bg-[#F8F8F8] w-full pl-10  py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <img
                src={SearchIcon}
                alt="search icon"
                className="absolute left-3 top-2.5"
              />
              <button className=" ">
                <img src={InputCell} alt="input cell" />
              </button>
            </div>

            {/* Global WebSocket Connection Status */}
            {/* <div className="mt-2 text-xs">
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  globalWsConnected
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full mr-1 ${
                    globalWsConnected ? "bg-green-400" : "bg-red-400"
                  }`}
                ></div>
                Global Notifications:{" "}
                {globalWsConnected ? "Connected" : "Disconnected"}
              </span>
              {globalWsError && (
                <div className="mt-1 text-red-600 text-xs">
                  Error: {globalWsError}
                </div>
              )}
            </div> */}
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredMessages.map((message) => {
              const isNewThread = newThreads.some(
                (thread) => thread.id === message.id
              );

              // Get the other participant's name for display
              const getDisplayName = () => {
                if (
                  !message.participants ||
                  !Array.isArray(message.participants)
                ) {
                  return (
                    message.name ||
                    message.last_message?.sender_name ||
                    "Unknown User"
                  );
                }

                // Get all participants except the current user
                const otherParticipants = message.participants.filter(
                  (participant) => participant.id !== userData?.id
                );

                // If there are 3 or more participants (including current user), show 2 names with comma separation
                if (message.participants.length >= 3) {
                  const firstTwoNames = otherParticipants
                    .slice(0, 2)
                    .map((participant) => participant.name || participant.email)
                    .filter((name) => name) // Filter out any undefined names
                    .join(", ");

                  return firstTwoNames || "Multiple Participants";
                }

                // For 2 participants (including current user), show the other participant's name
                if (message.participants.length === 2) {
                  const otherParticipant = otherParticipants[0];
                  if (otherParticipant) {
                    return (
                      otherParticipant.name ||
                      otherParticipant.email ||
                      message.last_message?.sender_name ||
                      "Unknown User"
                    );
                  }
                  // Fallback if otherParticipants is empty (edge case)
                  return (
                    message.name ||
                    message.last_message?.sender_name ||
                    (message.request ? `Request #${message.request}` : "Unknown User")
                  );
                }

                // Fallback for single participant or edge cases (e.g. only current user in thread)
                const otherParticipant = otherParticipants[0];
                return (
                  otherParticipant?.name ||
                  otherParticipant?.email ||
                  message.name ||
                  message.last_message?.sender_name ||
                  (message.request ? `Request #${message.request}` : "Unknown User")
                );
              };

              return (
                <div
                  key={message.id}
                  onClick={() => handleMessageClick(message.id)}
                  className={`p-4 border-b border-[#DADADA] cursor-pointer hover:bg-[#F8F8F8] ${
                    selectedMessage === message.id
                      ? "bg-[#F8F8F8] border-r border-[#3CAB5C]"
                      : ""
                  } ${
                    isNewThread ? "bg-blue-50 border-l-4 border-l-blue-500" : ""
                  }`}
                >
                  <div className="flex items-start tracking-[0.03em]  space-x-3">
                    <div className="relative">
                      <div className="w-[74px] h-[74px] rounded-full bg-[#3CAB5C] flex items-center justify-center text-white font-medium text-[24px]">
                        {getDisplayName()
                          ? getDisplayName().charAt(0).toUpperCase()
                          : "?"}
                      </div>
                      {(() => {
                        const threadId = message.threadId || message.id;
                        const unreadCount = threadUnreadCounts[threadId] || 0;
                        return unreadCount > 0;
                      })() && (
                        <div className="absolute -bottom-0 -right-0 w-4 h-4 bg-[#039855] rounded-full border-2 border-white"></div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <h3 className="md:text-[20px] text-[16px] tracking-[0.03em]  leading-[22px] font-medium text-[#242424] ">
                            {getDisplayName()}
                          </h3>
                          {isNewThread && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                              NEW
                            </span>
                          )}
                        </div>
                        <span className="text-[#595959] text-nowrap md:text-[16px]  tracking-[0.03em]  text-[14px] font-medium">
                          {/* Show real-time timestamp only for the specific thread that received the message */}
                          {(() => {
                            const threadId = message.threadId || message.id;
                            const realTimeData = realTimeMessages[threadId];

                            // Only use real-time timestamp if it's for this specific thread and is recent
                            if (realTimeData?.timestamp) {
                              const realTimeDate = new Date(
                                realTimeData.timestamp
                              );
                              const now = new Date();
                              const diffInMinutes =
                                (now - realTimeDate) / (1000 * 60);

                              // Only use real-time timestamp if it's very recent (within last 2 minutes)
                              if (diffInMinutes < 2) {
                                return formatRelativeTime(
                                  realTimeData.timestamp
                                );
                              }
                            }

                            // Fallback to original timestamp
                            return formatRelativeTime(
                              message.timestamp || message.last_message_at
                            );
                          })()}
                        </span>
                      </div>

                      <p className="text-[#333333] md:text-[16px] tracking-[0.03em]   leading-[20px] text-[14px] font-medium">
                        {humanizeEnum(message.issuetype)}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p
                            className={`md:text-[16px] truncate tracking-[0.03em]  leading-[20px] text-[14px] m-0 ${
                              message.hasOffer || message.hasJob
                                ? "text-[#3CAB5C] "
                                : "text-[#595959]"
                            }`}
                          >
                            {/* Show real-time message if available, otherwise show last message */}
                            {(() => {
                              const messageBody =
                                realTimeMessages[message.id]?.body ||
                                realTimeMessages[message.threadId]?.body ||
                                message.lastMessage ||
                                message.last_message?.body ||
                                message.last_message?.message ||
                                "No messages yet";
                              const messageSender =
                                realTimeMessages[message.id]?.sender_id ||
                                realTimeMessages[message.threadId]?.sender_id ||
                                message.last_message?.sender;
                              const isFromCurrentUser =
                                messageSender === userData?.id;

                              // Check if this is a proposal message
                              const isProposalMessage =
                                messageBody?.includes(
                                  "📩 New proposal submitted"
                                ) ||
                                messageBody?.includes("Amount:") ||
                                messageBody?.includes("Proposal ID:") ||
                                messageBody?.includes("proposed a change");

                              if (isProposalMessage) {
                                // Extract proposal data for display
                                const amountMatch =
                                  messageBody.match(/Amount:\s*([^\n]+)/) ||
                                  messageBody.match(/New estimate:\s*([^\n]+)/);
                                const timeframeMatch = messageBody.match(
                                  /Timeframe:\s*([^\n]+)/
                                );
                                const proposalIdMatch = messageBody.match(
                                  /Proposal ID:\s*([^\n]+)/
                                );
                                const requestIssueTypeMatch = message.issuetype;
                                // Humanize the issue type (e.g., PEST_CONTROL -> Pest Control)
                                const humanizeIssueType = (val) => {
                                  if (!val || typeof val !== "string") return val;
                                  return val
                                    .replace(/[_\s]+/g, " ")
                                    .toLowerCase()
                                    .split(" ")
                                    .filter(Boolean)
                                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                                    .join(" ");
                                };

                                const proposalTitle = `Proposal (${(
                                  humanizeIssueType(requestIssueTypeMatch) || "Unknown"
                                )})`;
                                const proposalAmount =
                                  amountMatch?.[1] ? formatProposalAmount(amountMatch[1]) : "Not specified";
                                const proposalTimeframe =
                                  timeframeMatch?.[1] ? formatScheduledTime(timeframeMatch[1].trim()) : "Not specified";

                                return isFromCurrentUser
                                  ? `You: ${proposalTitle} - ${proposalAmount}`
                                  : `${proposalTitle} - ${proposalAmount}`;
                              }

                              const safeMessageBody = formatMessageBody(String(messageBody || ""));
                              const invoiceInfo = extractInvoiceLink(safeMessageBody);
                              const previewBody = invoiceInfo ? invoiceInfo.text : safeMessageBody;
                              const displayText =
                                previewBody.length > 20
                                  ? previewBody.slice(0, 20) + "..."
                                  : previewBody;

                              return isFromCurrentUser
                                ? `You: ${displayText}`
                                : displayText;
                            })()}
                          </p>
                        </div>

                        {/* Unread count indicator - only show if not currently viewing this specific chat */}
                        {(() => {
                          const threadId = message.threadId || message.id;
                          const unreadCount = threadUnreadCounts[threadId] || 0;
                          const shouldShow =
                            unreadCount > 0 && selectedItemId !== message.id;

                          // Debug logging
                          if (unreadCount > 0) {
                            console.log(
                              `[Messages] Thread ${threadId} has unread count: ${unreadCount}, shouldShow: ${shouldShow}, selectedItemId: ${selectedItemId}`
                            );
                          }

                          return shouldShow;
                        })() && (
                          <div className="ml-2 flex-shrink-0">
                            <div className="bg-[#3CAB5C] text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-2 animate-pulse">
                              {(() => {
                                const threadId = message.threadId || message.id;
                                const unreadCount =
                                  threadUnreadCounts[threadId] || 0;
                                return unreadCount > 99 ? "99+" : unreadCount;
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Show "READ" indicator for currently viewed chat */}
                        {(() => {
                          const threadId = message.threadId || message.id;
                          const isCurrentlyViewing =
                            selectedThreadId === threadId ||
                            selectedItemId === message.id;
                          const unreadCount = threadUnreadCounts[threadId] || 0;
                          return isCurrentlyViewing && unreadCount === 0;
                        })() && (
                          <div className="ml-2 flex-shrink-0">
                            {/* <div className="bg-gray-400 text-white text-xs rounded-full px-2 py-1">
                              READ
                            </div> */}
                          </div>
                        )}

                        {/* Show typing indicator if someone is typing in this thread */}
                        {messageListTyping[message.id] && (
                          <div className="flex items-center space-x-1 text-[#3CAB5C] text-sm">
                            <div className="flex space-x-1">
                              <div className="w-1 h-1 bg-[#3CAB5C] rounded-full animate-bounce"></div>
                              <div
                                className="w-1 h-1 bg-[#3CAB5C] rounded-full animate-bounce"
                                style={{ animationDelay: "0.1s" }}
                              ></div>
                              <div
                                className="w-1 h-1 bg-[#3CAB5C] rounded-full animate-bounce"
                                style={{ animationDelay: "0.2s" }}
                              ></div>
                            </div>
                            <span className="text-xs">typing...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat Content - Show on desktop always, on mobile only when showChatList is false */}
        <div
          className={`lg:flex-[2.5] md:flex-[1.5] relative w-full flex flex-col overflow-scroll ${
            showChatList ? "hidden md:flex" : "flex"
          }`}
        >
          {selectedMessageData ? (
            <>
              {/* Header */}
              <div className="p-4 border-b absolute w-full border-[#DADADA] flex-shrink-0 bg-white">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleBackClick}
                    className="md:hidden p-2 hover:bg-gray-100 rounded-lg mr-2"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-[#3CAB5C] flex items-center justify-center text-white font-medium text-[16px]">
                      {selectedMessageData.name
                        ? selectedMessageData.name.charAt(0).toUpperCase()
                        : "?"}
                    </div>
                    {/* {selectedMessageData && (
                      <div
                        className={`absolute -bottom-0 -right-0 w-3 h-3 rounded-full border-2 border-white ${(() => {
                          const threadId =
                            selectedMessageData.threadId ||
                            selectedMessageData.id;
                          const unreadCount = threadUnreadCounts[threadId] || 0;
                          return unreadCount > 0
                            ? "bg-[#039855]"
                            : "bg-gray-400";
                        })()}`}
                      ></div>
                    )} */}
                  </div>

                  <div>
                    <div className="flex flex-col gap-2">
                      <h3 className="font-medium text-[#242424] md:text-[20px] text-[16px]">
                        {selectedMessageData.name}
                      </h3>
                    </div>
                    <div className="flex sm2:items-center space-x-3 flex-col sm2:flex-row">
                      <p className="text-[#333333] font-medium md:text-[16px] text-[14px]">
                        {humanizeEnum(
                          selectedMessageData.issuetype ||
                            selectedMessageData.service
                        )}
                      </p>
                      {/* <div className="w-px h-4 bg-gray-300 mx-2 sm2:block hidden"></div> */}
                      {/* <div className="flex items-center space-x-1">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            isUserOnline(selectedMessageData)
                              ? "bg-[#039855]"
                              : "bg-gray-400"
                          }`}
                        ></div>
                        <span
                          className={`text-sm font-medium ${
                            isUserOnline(selectedMessageData)
                              ? "text-[#039855]"
                              : "text-gray-500"
                          }`}
                        >
                          {isUserOnline(selectedMessageData)
                            ? "Online"
                            : "Offline"}
                        </span>
                      </div> */}
                      {/* <div className="flex items-center ">
                          <span className="text-[#333333]  font-medium md:text-[16px] text-[14px]">
                            Rate {selectedMessageData.rating}/5
                          </span>
                          {renderStars(selectedMessageData.rating)}
                        </div> */}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 h-full flex flex-col bg-[#F8F8F8] pt-[87px]">
                {/* Job/Offer details */}

                {/* Chat Section */}
                <div className="flex flex-col h-full font-neue-haas bg-[#F8F8F8]">
                  {/* Messages scrollable area */}
                  <div
                    ref={messagesContainerRef}
                    className="flex-1 overflow-y-auto p-4 font-neue-haas space-y-2 min-h-0"
                  >
                    {isLoadingHistory && chatMessages.length === 0 ? (
                      <div className="flex justify-center items-center h-32">
                        <div className="text-[#595959]">
                          Loading messages...
                        </div>
                      </div>
                    ) : (
                      chatMessages.map((msg, index) => {
                        const prevMsg =
                          index > 0 ? chatMessages[index - 1] : null;
                        const isFirstInGroup =
                          !prevMsg ||
                          prevMsg.sender_id !== msg.sender_id ||
                          new Date(prevMsg.created_at).toDateString() !==
                            new Date(msg.created_at).toDateString();

                        return (
                          <div
                            key={msg.id}
                            className={`flex ${
                              isUser(msg) ? "justify-end" : "justify-start"
                            }`}
                          >
                            <div className="flex flex-col font-neue-haas gap-2">
                              {isFirstInGroup && (
                                <div
                                  className={`text-xs opacity-75 mb-1 flex ${
                                    isUser(msg)
                                      ? "justify-end"
                                      : "justify-start"
                                  }`}
                                >
                                  {isUser(msg) ? (
                                    <span className="text-[#242424] mr-2 font-medium md:text-[16px] text-[14px]">
                                      You
                                    </span>
                                  ) : (
                                    <span className="text-[#242424] mr-2 font-medium md:text-[16px] text-[14px]">
                                      {msg.sender_name || selectedMessageData?.name || "Other User"}
                                    </span>
                                  )}
                                  <span className="text-[#767676] mr-2 font-medium md:text-[16px] text-[14px]">
                                    {new Date(
                                      msg.created_at
                                    ).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </span>
                                </div>
                              )}
                              {/* Reschedule Request card removed per requirement; button appended within message bubble below */}

                              {/* Check if this message is a proposal message */}
                              {(() => {
                                const isProposalMessage =
                                  msg.body?.includes(
                                    "📩 New proposal submitted"
                                  ) ||
                                  msg.body?.includes("Amount:") ||
                                  msg.body?.includes("Proposal ID:") ||
                                  msg.body?.includes("proposed a change");

                                if (isProposalMessage) {
                                  // Parse proposal data
                                  const body = msg.body;
                                  const amountMatch =
                                    body.match(/Amount:\s*([^\n]+)/) ||
                                    body.match(/New estimate:\s*([^\n]+)/);
                                  const timeframeMatch = body.match(
                                    /Timeframe:\s*([^\n]+)/
                                  );
                                  const messageMatch =
                                    body.match(/Message:\s*([^\n]+)/) ||
                                    body.match(/Additional work:\s*([^\n]+)/);
                                  const proposalIdMatch = body.match(
                                    /Proposal ID:\s*([^\n]+)/
                                  );

                                  const proposalData = {
                                    title: `Job: ${
                                      humanizeEnum(
                                        selectedMessageData.issuetype ||
                                          selectedMessageData.issueType
                                      ) || "Unknown"
                                    }`,
                                    date: new Date(
                                      msg.created_at
                                    ).toLocaleDateString(),
                                    estimatedCost:
                                      amountMatch?.[1] ? formatProposalAmount(amountMatch[1]) : "Not specified",
                                    timeframe:
                                      timeframeMatch?.[1] ? formatScheduledTime(timeframeMatch[1].trim()) : "Not specified",
                                    message:
                                      messageMatch?.[1] ||
                                      "No message provided",
                                    id: proposalIdMatch?.[1] || null,
                                  };

                                  console.log('messages proposal data', {proposalData})

                                  // Render proposal message in special format
                                  return (
                                    <div className="max-w-[675px] w-full overflow-hidden border border-[#DADADA] rounded-[8px]">
                                      <div className="bg-[#F8F8F8] flex gap-4 p-4">
                                        {/*<div className="sm2:bg-[#ECECEC] rounded-full w-fit">*/}
                                        {/*  <img*/}
                                        {/*    className="p-3"*/}
                                        {/*    src={FileIcon}*/}
                                        {/*    alt="file icon"*/}
                                        {/*  />*/}
                                        {/*</div>*/}
                                        <div>
                                          <h2 className="text-[#242424] font-medium md:text-[20px] text-[16px]">
                                            {proposalData.title}
                                          </h2>
                                          <p className="text-[#595959] font-medium md:text-[16px] text-[14px]">
                                            Date: {proposalData.date}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="p-4 bg-white">
                                        <div className="space-y-4">
                                          <h1 className="md:text-[20px] text-[16px] font-medium text-[#242424]">
                                            Proposal Summary
                                          </h1>
                                          <div className="flex sm2:items-center sm2:flex-row flex-col justify-between">
                                            <div className="flex flex-col gap-1">
                                              <span className="text-[12px] font-semibold text-[#333333]">
                                                Estimated Cost:
                                              </span>
                                              <span className="text-sm text-gray-600">
                                                {proposalData.estimatedCost}
                                              </span>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                              <span className="text-[12px] font-semibold text-[#333333]">
                                                Estimated Timeframe:
                                              </span>
                                              <span className="text-sm text-gray-600">
                                                {proposalData.timeframe}
                                              </span>
                                            </div>
                                          </div>
                                          <div>
                                            <h3 className="text-[12px] font-semibold text-[#333333] mb-1">
                                              Message:
                                            </h3>
                                            <p className="md:text-[14px] text-[12px] text-[#595959] font-medium whitespace-pre-line">
                                              {formatMessageBody(proposalData.message)}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                      {(() => {
                                        const action = msg.ui_action;
                                        const isChangeProposalAction =
                                          action &&
                                          String(action.type).toUpperCase() === "CHANGE_PROPOSAL";

                                        const rawStatus =
                                          localProposalStatus ?? selectedMessageData?.change_proposal?.status ?? null;
                                        const statusNorm =
                                          rawStatus === null || rawStatus === undefined
                                            ? null
                                            : String(rawStatus).toUpperCase();
                                        const isDecided = [
                                          "ACCEPTED",
                                          "APPROVED",
                                          "DENIED",
                                          "REJECTED",
                                          "DECLINED",
                                        ].includes(statusNorm || "");

                                        const shouldShow =
                                          !isDecided &&
                                          !isUser(msg) &&
                                          (canRespondToChange && isChangeProposalAction);

                                        if (!shouldShow) return null;

                                        return (
                                          <div className="py-3 px-4 border-t border-[#ECECEC] flex justify-start">
                                            <ButtonComponent
                                              bgColor="#3CAB5C"
                                              color="#FFFFFF"
                                              borderColor="#3CAB5C"
                                              onClick={() => handleOpenRespondModal(proposalData, action)}
                                            >
                                              Respond to Change Request
                                            </ButtonComponent>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  );
                                }

                                // Render regular message
                                return (
                                  <div
                                    className={`sm2:max-w-xs max-w-[260px] md:max-w-[22rem]  lg:max-w-xl px-4 py-4 font-medium rounded-[12px] ${
                                      isUser(msg)
                                        ? "bg-[#3CAB5C] text-white rounded-tr-[2px]"
                                        : "bg-[#FFFFFF] border border-[#ECECEC] text-[#242424] rounded-tl-[2px]"
                                    }`}
                                  >
                                    {(() => {
                                      const formattedBody = formatMessageBody(msg.body);
                                      const invoiceInfo = extractInvoiceLink(formattedBody);
                                      const displayBody = invoiceInfo
                                        ? invoiceInfo.text
                                        : formattedBody;

                                      return (
                                        <>
                                          <p className="text-[16px] font-medium break-words whitespace-pre-wrap">
                                            {displayBody}
                                          </p>
                                          {invoiceInfo?.url && (
                                            <div className="mt-3">
                                              <a
                                                href={invoiceInfo.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center justify-center bg-[#3CAB5C] hover:bg-[#2F9A4D] text-white hover:text-white visited:text-white active:text-white px-4 py-2 rounded-[8px] text-[14px] font-semibold no-underline hover:no-underline transition-colors duration-200"
                                              >
                                                View invoice
                                              </a>
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                    {(() => {
                                      const action = msg.ui_action;
                                      const isChangeProposalAction =
                                        action &&
                                        String(action.type).toUpperCase() === "CHANGE_PROPOSAL";

                                      const rawStatus =
                                        localProposalStatus ?? selectedMessageData?.change_proposal?.status ?? null;
                                      const statusNorm =
                                        rawStatus === null || rawStatus === undefined
                                          ? null
                                          : String(rawStatus).toUpperCase();
                                      const isDecided = [
                                        "ACCEPTED",
                                        "APPROVED",
                                        "DENIED",
                                        "REJECTED",
                                        "DECLINED",
                                      ].includes(statusNorm || "");

                                      const shouldShow =
                                        !isDecided &&
                                        !isUser(msg) &&
                                        (canRespondToChange && isChangeProposalAction);
                                      if (!shouldShow) return null;

                                      return (
                                        <div className="mt-3">
                                          <ButtonComponent
                                            bgColor="#3CAB5C"
                                            color="#FFFFFF"
                                            borderColor="#3CAB5C"
                                            onClick={() => handleOpenRespondModal(null, action)}
                                          >
                                            Respond to Change Request
                                          </ButtonComponent>
                                        </div>
                                      );
                                    })()}
                                    {/* Append Reschedule Respond button inside the same bubble when applicable */}
                                    {(() => {
                                      // Only show Approve/Deny for messages that include the reschedule ui_action
                                      const action = msg.ui_action;
                                      const isRescheduleAction =
                                        action && String(action.type).toUpperCase() === "RESCHEDULE_REQUEST";
                                      const shouldShowReschedule = canRespondToReschedule && isRescheduleAction && !isUser(msg);
                                      if (!shouldShowReschedule) return null;

                                      // Prefer message-linked reschedule record; fallback to thread-level
                                      const rs = msg?.reschedule_request || selectedMessageData?.reschedule_request || {};

                                      // Determine if the reschedule has already been accepted, either locally (optimistic)
                                      // or from the server-provided status. If accepted, we should disable the button.
                                      const rawStatus =
                                        (localRescheduleStatus ?? rs?.status ?? selectedMessageData?.reschedule_request?.status ?? null);
                                      const statusNorm = rawStatus == null ? null : String(rawStatus).toUpperCase();
                                      const isAccepted = statusNorm === "ACCEPTED" || statusNorm === "APPROVED";

                                      return (
                                        <div className="mt-3">
                                          <ButtonComponent
                                            bgColor="#3CAB5C"
                                            color="#FFFFFF"
                                            borderColor="#3CAB5C"
                                            disabled={isAccepted}
                                            onClick={() => {
                                              if (isAccepted) return;
                                              handleOpenRescheduleModal(rs, action);
                                            }}
                                          >
                                            Respond to Reschedule Request
                                          </ButtonComponent>
                                        </div>
                                      );
                                    })()}
                                    {(() => {
                                      const action = msg.ui_action;
                                      const isWarrantyAction =
                                        action &&
                                        String(action.type).toUpperCase() === "WARRANTY_REQUEST";
                                      const shouldShowWarranty =
                                        canReviewWarrantyClaim &&
                                        isWarrantyAction &&
                                        !isUser(msg);
                                      if (!shouldShowWarranty) return null;

                                      return (
                                        <div className="mt-3">
                                          <ButtonComponent
                                            bgColor="#3CAB5C"
                                            color="#FFFFFF"
                                            borderColor="#3CAB5C"
                                            onClick={() => handleOpenWarrantyModal(action)}
                                          >
                                            View Warranty Details
                                          </ButtonComponent>
                                        </div>
                                      );
                                    })()}
                                    {(() => {
                                      const action = msg.ui_action;
                                      const isWarrantyScheduleAction =
                                        action &&
                                        String(action.type).toUpperCase() === "WARRANTY_SCHEDULE";
                                      const shouldShowWarrantySchedule =
                                        canReviewWarrantyClaim &&
                                        isWarrantyScheduleAction;
                                      if (!shouldShowWarrantySchedule) return null;

                                      return (
                                        <div className="mt-3">
                                          <ButtonComponent
                                            bgColor={isUser(msg) ? "#FFFFFF" : "#3CAB5C"}
                                            color={isUser(msg) ? "#3CAB5C" : "#FFFFFF"}
                                            borderColor="#3CAB5C"
                                            onClick={() => handleOpenWarrantyScheduleModal(action)}
                                          >
                                            Schedule Warranty Repair
                                          </ButtonComponent>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })
                    )}

                    {/* Typing indicator */}
                    {Object.keys(typingUsers).length > 0 && (
                      <div className="flex justify-start">
                        <div className="flex flex-col font-neue-haas gap-2">
                          <div className="text-xs opacity-75 mb-1 flex justify-start">
                            <span className="text-[#242424] mr-2 font-medium md:text-[16px] text-[14px]">
                              {Object.values(typingUsers).join(", ")}
                            </span>
                            <span className="text-[#767676] mr-2 font-medium md:text-[16px] text-[14px]">
                              {Object.keys(typingUsers).length === 1
                                ? "is"
                                : "are"}{" "}
                              typing...
                            </span>
                          </div>
                          <div className="bg-[#FFFFFF] border border-[#ECECEC] text-[#242424] rounded-tl-[2px] px-4 py-4 font-medium rounded-[12px]">
                            <div className="flex items-center space-x-1">
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                <div
                                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                  style={{ animationDelay: "0.1s" }}
                                ></div>
                                <div
                                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                  style={{ animationDelay: "0.2s" }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div ref={messageEndRef} />
                  </div>

                  {/* Footer input always pinned */}
                  <div className="flex items-center space-x-2 px-4 py-2 bg-[#F8F8F8] border-t border-[#DADADA] flex-shrink-0">
                    <div className="relative w-full rounded-[20px] bg-white">
                      <input
                        ref={messageInputRef}
                        type="text"
                        placeholder="Type a message..."
                        value={messageInput}
                        onChange={onInputChange}
                        onKeyPress={onKeyPress}
                        disabled={!isConnected}
                        className="flex-1 p-4 pr-[7rem] w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      />
                      {/*<div className="absolute right-4 top-1/2 -translate-y-1/2 flex space-x-2">*/}
                      {/*  <button className="p-2 bg-transparent">*/}
                      {/*    <img src={FaceNeutral} alt="face neutral" />*/}
                      {/*  </button>*/}
                      {/*  <button className="p-2 bg-transparent">*/}
                      {/*    <img src={Attachment} alt="attachment" />*/}
                      {/*  </button>*/}
                      {/*</div>*/}
                    </div>
                    <button
                      onClick={handleSendMessage}
                      disabled={!isConnected || !messageInput.trim()}
                      className="p-4 bg-[#3CAB5C] text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <img src={Send} alt="send" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-[#F8F8F8]">
              <div className="text-center">
                <div className="text-[#595959] text-lg mb-2">
                  Select a conversation
                </div>
                <div className="text-[#767676] text-sm">
                  Choose a conversation from the list to start messaging
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    {/* Modal: Respond to Change Request (owners only) */}
    {isOwner && (
      <ProposalRequestAcceptDeny
        open={showRespondModal}
        onClose={handleCloseRespondModal}
        proposal={respondProposal}
        onAccept={handleAcceptChange}
        onDeny={handleDenyChange}
      />
    )}
    {/* Modal: Respond to Reschedule Request (owners only) */}
    {isOwner && (
      <RescheduleRequestAcceptDeny
        open={showRescheduleModal}
        onClose={handleCloseRescheduleModal}
        reschedule={rescheduleToRespond ?? selectedMessageData?.reschedule_request}
        onAccept={handleAcceptReschedule}
        onDeny={handleDenyReschedule}
      />
    )}
    <WarrantyClaimReviewModal
      open={showWarrantyModal}
      onClose={handleCloseWarrantyModal}
      claim={warrantyClaim}
      loading={warrantyClaimLoading}
      reviewNotes={warrantyReviewNotes}
      onReviewNotesChange={setWarrantyReviewNotes}
      allowReview={canReviewWarrantyClaim}
      extraFooterActions={
        String(warrantyClaim?.status || "").toUpperCase() === "APPROVED" ? (
          <ButtonComponent
            bgColor="#3CAB5C"
            color="#FFFFFF"
            borderColor="#3CAB5C"
            paddingY="10px"
            onClick={() => {
              setShowWarrantyModal(false);
              handleOpenWarrantyScheduleModal({
                request_id: warrantyClaim?.maintenanceRequestDetails?.id,
              });
            }}
          >
            Schedule Warranty Repair
          </ButtonComponent>
        ) : null
      }
      onApprove={handleApproveWarranty}
      onDeny={handleDenyWarranty}
      decisionLoading={warrantyDecisionSubmitting}
    />
    <WarrantyScheduleRepairModal
      open={showWarrantyScheduleModal}
      onClose={handleCloseWarrantyScheduleModal}
      onSubmit={handleSubmitWarrantySchedule}
      submitting={warrantyScheduleSubmitting}
    />
  </div>
  );
};

export default Messages;
